import { Server, Cpu, CheckCircle2, AlertCircle } from 'lucide-react'
import {
  Card,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import type { InfrastructureSummary as SummaryData } from '@/types/infrastructure'

interface SummaryCardProps {
  readonly title: string
  readonly value: number
  readonly description: string
  readonly icon: React.ElementType
  readonly isLoading?: boolean
}

function SummaryCard({ title, value, description, icon: Icon, isLoading }: SummaryCardProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-8 w-16" />
        </CardHeader>
        <CardFooter>
          <Skeleton className="h-3 w-32" />
        </CardFooter>
      </Card>
    )
  }

  return (
    <Card className="@container/card">
      <CardHeader>
        <CardDescription className="flex items-center gap-1.5">
          <Icon className="size-3.5" />
          {title}
        </CardDescription>
        <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">
          {value}
        </CardTitle>
      </CardHeader>
      <CardFooter className="text-sm">
        <div className="text-muted-foreground">{description}</div>
      </CardFooter>
    </Card>
  )
}

interface InfrastructureSummaryProps {
  readonly summary: SummaryData | undefined
  readonly isLoading: boolean
}

/**
 * Summary metric bar showing 4 infrastructure KPIs in a responsive grid
 */
export function InfrastructureSummary({ summary, isLoading }: InfrastructureSummaryProps) {
  const disconnectedPdps = (summary?.totalPdps ?? 0) - (summary?.connectedPdps ?? 0)
  const inactivePeps = (summary?.totalPeps ?? 0) - (summary?.activePeps ?? 0)

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <SummaryCard
        title="Total PDPs"
        value={summary?.totalPdps ?? 0}
        description="Policy Decision Points registered"
        icon={Server}
        isLoading={isLoading}
      />
      <SummaryCard
        title="Connected PDPs"
        value={summary?.connectedPdps ?? 0}
        description={disconnectedPdps > 0 ? `${disconnectedPdps} disconnected` : 'All PDPs healthy'}
        icon={CheckCircle2}
        isLoading={isLoading}
      />
      <SummaryCard
        title="Total PEPs"
        value={summary?.totalPeps ?? 0}
        description="Policy Enforcement Points tracked"
        icon={Cpu}
        isLoading={isLoading}
      />
      <SummaryCard
        title="Active PEPs"
        value={summary?.activePeps ?? 0}
        description={inactivePeps > 0 ? `${inactivePeps} inactive` : 'All PEPs healthy'}
        icon={inactivePeps > 0 ? AlertCircle : CheckCircle2}
        isLoading={isLoading}
      />
    </div>
  )
}
